import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import { blockedEmailsService } from "../../services/blocked-emails.service";
import { systemService } from "../../services/system.service";
import { BlockedEmail } from "../../types/blocked-email";
import { useAuth } from "../../context/AuthContext";
import { Permission } from "../../permissions/permission.enum";
import { getErrorMsg } from "../../utils/error";

// ponytail: loose client-side shape check only — the API's Joi schema is the real gate.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BlockedEmailsPage = () => {
  const { t } = useTranslation("common");
  const { hasPermission } = useAuth();
  const canEdit = hasPermission(Permission.SYSTEM_SETTINGS_UPDATE);

  const [rows, setRows] = useState<BlockedEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [requireVerifiedEmail, setRequireVerifiedEmail] = useState(false);
  const [securitySaving, setSecuritySaving] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [list, security] = await Promise.all([
        blockedEmailsService.list(),
        systemService.getRegistrationSecurityConfig(),
      ]);
      setRows(list);
      setRequireVerifiedEmail(security.requireVerifiedEmailForPhoneOtp);
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_load")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreate = () => {
    setEmail("");
    setFormError("");
    setModalOpen(true);
  };

  const saveRow = async () => {
    const value = email.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(value)) {
      setFormError(t("blocked_email_invalid"));
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    setFormError("");
    try {
      await blockedEmailsService.create(value);
      setModalOpen(false);
      setSuccess(t("saved"));
      await refresh();
    } catch (e) {
      setFormError(getErrorMsg(e, t("failed_to_save")));
    } finally {
      setSaving(false);
    }
  };

  const saveSecurity = async () => {
    setSecuritySaving(true);
    setError("");
    setSuccess("");
    try {
      const saved = await systemService.setRegistrationSecurityConfig({
        requireVerifiedEmailForPhoneOtp: requireVerifiedEmail,
      });
      setRequireVerifiedEmail(saved.requireVerifiedEmailForPhoneOtp);
      setSuccess(t("saved"));
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_save")));
    } finally {
      setSecuritySaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setError("");
    setSuccess("");
    try {
      await blockedEmailsService.remove(deleteId);
      setDeleteId(null);
      setSuccess(t("saved"));
      await refresh();
    } catch (e) {
      setError(getErrorMsg(e, t("failed_to_delete")));
    }
  };

  return (
    <Layout title={t("blocked_emails")}>
      <div className="space-y-6">
        {error && (
          <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-800">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm border border-green-100 dark:border-green-800">
            {success}
          </div>
        )}

        <Card className="p-5 space-y-4">
          <div>
            <h2 className="text-base font-semibold">{t("registration_security")}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t("registration_security_desc")}
            </p>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              checked={requireVerifiedEmail}
              disabled={loading || !canEdit}
              onChange={(e) => setRequireVerifiedEmail(e.target.checked)}
            />
            <span className="text-sm">{t("require_verified_email_for_phone_otp")}</span>
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t("require_verified_email_for_phone_otp_hint")}
          </p>
          {canEdit && (
            <div className="flex justify-end">
              <Button variant="primary" onClick={saveSecurity} disabled={loading || securitySaving}>
                {securitySaving ? t("saving") : t("save")}
              </Button>
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-semibold">{t("blocked_emails")}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t("blocked_emails_desc")}
              </p>
            </div>
            {canEdit && (
              <Button variant="primary" onClick={openCreate}>
                {t("add_blocked_email")}
              </Button>
            )}
          </div>

          {loading ? (
            <p className="text-sm text-gray-500" aria-live="polite">{t("loading")}</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500">{t("noData")}</p>
          ) : (
            <div className="overflow-x-auto themed-scrollbar-prominent">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                    <th className="pb-2 font-medium">{t("blocked_email_address")}</th>
                    {canEdit && <th className="pb-2 font-medium w-28">{t("actions")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b dark:border-gray-700">
                      <td className="py-2 pr-4 font-mono">{row.email}</td>
                      {canEdit && (
                        <td className="py-2">
                          <button
                            onClick={() => setDeleteId(row.id)}
                            className="text-red-500 hover:underline text-xs"
                          >
                            {t("delete")}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} title={t("add_blocked_email")}>
        <div className="space-y-4">
          {formError && (
            <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
              {formError}
            </div>
          )}
          <div>
            <Input
              label={`${t("blocked_email_address")} *`}
              value={email}
              placeholder="abc@gmail.com"
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {t("blocked_email_hint")}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              {t("cancel")}
            </Button>
            <Button variant="primary" onClick={saveRow} disabled={saving}>
              {saving ? t("saving") : t("save")}
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} title={t("delete")}>
        <div className="space-y-4">
          <p className="text-sm">{t("delete_blocked_email_confirm")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteId(null)}>
              {t("cancel")}
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default BlockedEmailsPage;
